import {
  Table,
  TableColumn,
  InfoCard,
  EmptyState,
} from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import Typography from '@material-ui/core/Typography';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';

interface HelmParameter {
  name: string;
  type: string;
  description?: string;
  default?: unknown;
  enum?: string[];
  minimum?: number;
  maximum?: number;
  pattern?: string;
  required?: boolean;
  group?: string;
}

interface ParameterGroup {
  name: string;
  title?: string;
  parameters: string[];
}

/** Returns true if the entity is a helm-chart Resource with extracted parameters */
export function isHelmChartWithParameters(entity: Entity): boolean {
  if (entity.kind !== 'Resource') return false;
  const spec = entity.spec as Record<string, unknown> | undefined;
  return spec?.type === 'helm-chart';
}

const typeColors: Record<string, 'primary' | 'default' | 'secondary'> = {
  string: 'primary',
  integer: 'secondary',
  number: 'secondary',
  boolean: 'default',
  array: 'default',
};

const columns: TableColumn<HelmParameter>[] = [
  {
    title: 'Name',
    field: 'name',
    render: row => (
      <Typography variant="body2" style={{ fontFamily: 'monospace', fontWeight: 500 }}>
        {row.name}
      </Typography>
    ),
  },
  {
    title: 'Type',
    field: 'type',
    render: row => (
      <Chip
        label={row.type}
        size="small"
        color={typeColors[row.type] ?? 'default'}
        variant="outlined"
      />
    ),
  },
  {
    title: 'Default',
    field: 'default',
    render: row => (
      <Typography variant="body2" style={{ fontFamily: 'monospace' }}>
        {row.default !== undefined ? String(row.default) : '—'}
      </Typography>
    ),
  },
  {
    title: 'Required',
    field: 'required',
    render: row =>
      row.required ? (
        <CheckCircleIcon fontSize="small" style={{ color: '#4caf50' }} />
      ) : (
        <Typography variant="body2" color="textSecondary">—</Typography>
      ),
  },
  {
    title: 'Allowed Values',
    render: row =>
      row.enum ? (
        <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
          {row.enum.map(v => (
            <Chip key={v} label={v} size="small" variant="outlined" />
          ))}
        </Box>
      ) : row.minimum !== undefined || row.maximum !== undefined ? (
        <Typography variant="body2" color="textSecondary">
          {row.minimum !== undefined ? `min: ${row.minimum}` : ''}
          {row.minimum !== undefined && row.maximum !== undefined ? ' · ' : ''}
          {row.maximum !== undefined ? `max: ${row.maximum}` : ''}
        </Typography>
      ) : (
        <Typography variant="body2" color="textSecondary">—</Typography>
      ),
  },
  {
    title: 'Description',
    field: 'description',
    render: row => (
      <Typography variant="body2" color="textSecondary">
        {row.description ?? '—'}
      </Typography>
    ),
  },
];

/**
 * Full-page content component that renders a read-only table of all
 * configurable Helm chart parameters extracted from values.yaml.
 */
export const HelmChartParametersContent = () => {
  const { entity } = useEntity();

  const spec = entity.spec as Record<string, unknown> | undefined;
  const definition = spec?.definition as Record<string, unknown> | undefined;
  const parameters = (definition?.parameters ?? []) as HelmParameter[];
  const parameterGroups = (definition?.parameterGroups ?? []) as ParameterGroup[];
  const chartPath = entity.metadata.annotations?.['helm.backstage.io/chart-path'] ?? 'deploy';

  if (parameters.length === 0) {
    return (
      <EmptyState
        title="No parameters available"
        description="This chart has no configurable parameters yet. Parameters are automatically extracted from values.yaml during catalog processing. Ensure the chart repository contains a values.yaml file and check back after the next catalog refresh."
        missing="data"
      />
    );
  }

  // Group parameters
  const paramToGroup = new Map<string, string>();
  for (const g of parameterGroups) {
    for (const pName of g.parameters) {
      paramToGroup.set(pName, g.name);
    }
  }

  const groupTitles = new Map<string, string>();
  for (const g of parameterGroups) {
    groupTitles.set(g.name, g.title ?? g.name);
  }

  const grouped = new Map<string, HelmParameter[]>();
  const ungrouped: HelmParameter[] = [];

  for (const param of parameters) {
    const groupName = paramToGroup.get(param.name) ?? param.group;
    if (groupName) {
      const existing = grouped.get(groupName) ?? [];
      existing.push(param);
      grouped.set(groupName, existing);
    } else {
      ungrouped.push(param);
    }
  }

  return (
    <Box>
      <Box style={{ marginBottom: 16 }}>
        <Typography variant="body2" color="textSecondary">
          {parameters.length} configurable parameter{parameters.length !== 1 ? 's' : ''} extracted
          from <strong>{chartPath}/values.yaml</strong>. These are the values
          available to override when deploying this chart.
        </Typography>
      </Box>

      {ungrouped.length > 0 && (
        <Box style={{ marginBottom: 16 }}>
          <InfoCard title="General">
            <Table
              data={ungrouped}
              columns={columns}
              options={{
                paging: false,
                search: false,
                toolbar: false,
                padding: 'dense',
              }}
            />
          </InfoCard>
        </Box>
      )}

      {Array.from(grouped.entries()).map(([groupName, params]) => (
        <Box key={groupName} style={{ marginBottom: 16 }}>
          <InfoCard
            title={groupTitles.get(groupName) ?? groupName}
            subheader={`${params.length} parameter${params.length !== 1 ? 's' : ''}`}
          >
            <Table
              data={params}
              columns={columns}
              options={{
                paging: false,
                search: false,
                toolbar: false,
                padding: 'dense',
              }}
            />
          </InfoCard>
        </Box>
      ))}
    </Box>
  );
};
